import { z } from "zod";
import {
  IntakeCategoryType,
  IntakeQuestionType,
} from "@src/sudozi-schemas/intake-submission-schemas";
import { RequestStatusSchema } from "@src/sudozi-schemas/request-status-schema";

const SubmissionAnswer = z.object({
  id: z.string(),
  stepLabel: z.string(),
  stepOrdinal: z.number(),
  questionId: z.string().nullable(),
  questionLabel: z.string(),
  questionOrdinal: z.number(),
  note: z.string().nullable(),
  commentCount: z.number(),
  version: z.number(),
});

const AnswerAddress = SubmissionAnswer.merge(
  z.object({
    intakeQuestionType: z.literal(IntakeQuestionType.ADDRESS),
    value: z
      .object({
        address1: z.string(),
        address2: z.string(),
        city: z.string(),
        state: z.string(),
        postalCode: z.string(),
        country: z.string(),
      })
      .nullable(),
  }),
);

const AnswerNeedsRequestSigner = SubmissionAnswer.merge(
  z.object({
    intakeQuestionType: z.literal(IntakeQuestionType.NEEDS_REQUEST_SIGNER),
    value: z.boolean().nullable(),
  }),
);

const AnswerYesNo = SubmissionAnswer.merge(
  z.object({
    intakeQuestionType: z.literal(IntakeQuestionType.YES_NO),
    value: z.boolean().nullable(),
  }),
);

const AnswerNumeric = SubmissionAnswer.merge(
  z.object({
    intakeQuestionType: z.enum([IntakeQuestionType.NUMBER]),
    value: z.number().nullable(),
  }),
);

const AnswerMoney = SubmissionAnswer.merge(
  z.object({
    intakeQuestionType: z.enum([
      IntakeQuestionType.MONEY,
      IntakeQuestionType.REQUEST_AMOUNT,
    ]),
    value: z
      .object({
        value: z.number(),
        currency: z.string().nullable(),
      })
      .nullable(),
  }),
);

const AnswerDateString = SubmissionAnswer.merge(
  z.object({
    intakeQuestionType: z.enum([
      IntakeQuestionType.START_DATE,
      IntakeQuestionType.END_DATE,
      IntakeQuestionType.DATE,
      IntakeQuestionType.DUE_DATE,
    ]),
    value: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .nullable(),
  }),
);

const AnswerDuration = SubmissionAnswer.merge(
  z.object({
    intakeQuestionType: z.literal(IntakeQuestionType.DURATION),
    value: z
      .object({
        years: z.number().nullable(),
        months: z.number().nullable(),
        days: z.number().nullable(),
      })
      .nullable(),
  }),
);

const AnswerString = SubmissionAnswer.merge(
  z.object({
    intakeQuestionType: z.union([
      z.literal(IntakeQuestionType.TEXT),
      z.literal(IntakeQuestionType.REQUEST_TITLE),
    ]),
    value: z.string().nullable(),
  }),
);

const AnswerEmail = SubmissionAnswer.merge(
  z.object({
    intakeQuestionType: z.literal(IntakeQuestionType.EMAIL),
    value: z.string().nullable(),
  }),
);

const AnswerRequestOwner = SubmissionAnswer.merge(
  z.object({
    intakeQuestionType: z.literal(IntakeQuestionType.REQUEST_OWNER_SELECT),
    value: z
      .object({
        id: z.string(),
        firstName: z.string(),
        lastName: z.string(),
        email: z.string(),
        label: z.string(),
      })
      .nullable(),
  }),
);

const AnswerDepartmentArray = SubmissionAnswer.merge(
  z.object({
    intakeQuestionType: z.literal(IntakeQuestionType.DEPARTMENT_SELECT),
    value: z.array(
      z.object({
        id: z.string(),
        label: z.string(),
        departmentApproverAssigneeUser: z
          .object({
            id: z.string(),
            firstName: z.string(),
            lastName: z.string(),
            email: z.string(),
          })
          .nullable(),
      }),
    ),
  }),
);

const AnswerStringArray = SubmissionAnswer.merge(
  z.object({
    intakeQuestionType: z.enum([
      IntakeQuestionType.DOCUMENTS,
      IntakeQuestionType.LOCATION_SELECT,
      IntakeQuestionType.MEMBER_SELECT,
      IntakeQuestionType.VENDOR_SELECT,
      IntakeQuestionType.SERVICE_SELECT,
      IntakeQuestionType.SPEND_CATEGORY_SELECT,
      IntakeQuestionType.BUSINESS_UNIT_SELECT,
      IntakeQuestionType.CUSTOM_SELECT,
      IntakeQuestionType.ENTITY_SELECT,
    ]),
    value: z
      .array(
        z.object({
          id: z.string(),
          label: z.string(),
        }),
      )
      .nullable(),
  }),
);

export const IntakeAnswer = z.union([
  AnswerAddress,
  AnswerDateString,
  AnswerDuration,
  AnswerNeedsRequestSigner,
  AnswerYesNo,
  AnswerNumeric,
  AnswerMoney,
  AnswerString,
  AnswerEmail,
  AnswerStringArray,
  AnswerRequestOwner,
  AnswerDepartmentArray,
]);
export type IntakeAnswerType = z.infer<typeof IntakeAnswer>;

export const GetIntakeSubmissionSchema = z.object({
  id: z.string(),
  title: z.string().nullable(),
  createdByUserId: z.string(),
  number: z.number(),
  isDraft: z.boolean(),
  archived: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
  version: z.number(),
  status: RequestStatusSchema,
  company: z.object({
    id: z.string(),
    name: z.string(),
  }),
  vendorCompany: z
    .object({
      id: z.string(),
      name: z.string(),
    })
    .nullable(),
  createdBy: z.object({
    id: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    email: z.string(),
  }),
  category: z.object({
    id: z.string(),
    name: z.string(),
    description: z.string(),
    type: IntakeCategoryType,
  }),
  intakeCategoryId: z.string(),
  watchers: z.array(
    z.object({
      id: z.string(),
      user: z.object({
        id: z.string(),
        firstName: z.string(),
        lastName: z.string(),
        email: z.string(),
      }),
    }),
  ),
  forVendor: z
    .object({
      id: z.string(),
      name: z.string(),
    })
    .nullable(),
  levelGroupStatusSendVendorFormTask: z
    .object({
      vendorFormSubmissions: z.array(
        z.object({ id: z.string(), status: RequestStatusSchema }),
      ),
    })
    .nullish(),
  associatedVendors: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
    }),
  ),
});

export type GetIntakeSubmissionSchemaType = z.infer<
  typeof GetIntakeSubmissionSchema
>;
